import { AnyAction, createAction, createReducer } from '@reduxjs/toolkit'
import { useLocalSearchParams, useNavigation, useRouter } from 'expo-router'
import React, { useEffect, useState } from 'react'
import { Dimensions } from 'react-native'
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view'
import { mvs, ScaledSheet } from 'react-native-size-matters'
import CacheableImage from '~components/CacheableImage'
import { FormButton } from '~components/FormButton'
import { FormInput } from '~components/FormInput'
import Loading from '~components/Loading'
import ModalScreen, { getModalStyleDefinition, ModalFooter } from '~components/ModalScreen'
import SortableGrid from '~components/SortableGrid'
import TabbedMenu from '~components/TabbedMenu'
import { Text, View } from '~components/Themed'
import { doc, firestore, onSnapshot, updateDoc } from '~providers/firebase'
import ChatService from '~services/chat'
import { useTheme } from '~theme/ThemeManager'
import { ExistingChat } from '~types'

export type EditChatViewType = 'details' | 'order'

export interface State {
  path: string
  view: EditChatViewType
  isLoading: boolean
  chat?: ExistingChat
}

export const initialState: State = {
  path: '',
  view: 'details',
  isLoading: true,
  chat: undefined,
}

export const setView = createAction<EditChatViewType>('setView')
export const setLoading = createAction<boolean>('setLoading')
export const setChat = createAction<ExistingChat>('setChat')

const reducer = createReducer(initialState, (builder) =>
  builder
    .addCase(setView, (state, action) => {
      state.view = action.payload
    })
    .addCase(setLoading, (state, action) => {
      state.isLoading = action.payload
    })
    .addCase(setChat, (state, action) => {
      state.chat = action.payload
      state.isLoading = false
    }),
)
const store = React.createContext<[State, React.Dispatch<AnyAction>]>([initialState, () => {}])
const { Provider } = store
const useEditChatContext = (): [State, React.Dispatch<AnyAction>] => React.useContext(store)

export default function EditChatScreen(): React.JSX.Element {
  const styles = useStyles()
  const { folderId, chatId, userId } = useLocalSearchParams<{
    folderId: string
    chatId: string
    userId?: string
  }>()
  const router = useRouter()
  const navigation = useNavigation()
  const [state, dispatch] = React.useReducer(reducer, {
    ...initialState,
    path: userId ? `users/${userId}/chats/${chatId}` : `classes/${folderId}/chats/${chatId}`,
  })
  const value: [State, React.Dispatch<AnyAction>] = [state, dispatch]

  const labels: EditChatViewType[] = ['details', 'order']
  const [name, setName] = useState('')

  const renderActiveTab = () => {
    switch (state.view) {
      case 'order':
        return <Order />
      case 'details':
      default:
        return (
          <KeyboardAwareScrollView>
            <View style={styles.inner}>
              <View style={styles.content}>
                <View style={styles.titleContainer}>
                  <Text style={styles.title}>Edit Chat</Text>
                </View>
                <View>
                  <FormInput
                    label="Name of chat"
                    value={name}
                    onChangeText={(text) => setName(text)}
                    maxLength={128}
                  />
                </View>
              </View>
            </View>
          </KeyboardAwareScrollView>
        )
    }
  }

  useEffect(() => {
    dispatch(setLoading(true))
    let cancel = false

    const subscriber = onSnapshot(
      doc(firestore, state.path),
      (snapshot) => {
        if (typeof snapshot.exists === 'function' ? !snapshot.exists() : !snapshot.exists) {
          router.canDismiss() && router.dismiss()
        } else if (!cancel) {
          const chat = ChatService.chatFromSnapshot(snapshot)
          dispatch(setChat(chat))
        }
      },
      () => {
        router.canDismiss() && router.dismiss()
      },
    )

    return () => {
      cancel = true
      subscriber()
    }
  }, [dispatch, router, state.path])

  const editChat = async () => {
    dispatch(setLoading(true))

    await ChatService.updateChat(state.path, {
      name,
    })

    router.canDismiss() && router.dismiss()
  }

  useEffect(() => {
    setName(state.chat?.name ?? '')
  }, [state.chat, setName])

  useEffect(() => {
    navigation.setOptions({ gestureEnabled: state.view !== 'order' })
  }, [navigation, state.view])

  return (
    <Provider value={value}>
      <ModalScreen
        ContainerComponent={React.Fragment}
        Footer={
          state.view === 'details' ? (
            <ModalFooter>
              <View></View>
              <FormButton
                title="Update Chat"
                onPress={editChat}
                disabled={state.isLoading || name.length === 0 || name === state.chat?.name}
              />
            </ModalFooter>
          ) : null
        }
      >
        <TabbedMenu<EditChatViewType>
          labels={labels}
          active={state.view}
          onPress={(index: number) => dispatch(setView(labels[index]))}
        />
        {!state.chat ? <Loading /> : renderActiveTab()}
      </ModalScreen>
    </Provider>
  )
}

const Order = () => {
  const [{ chat }] = useEditChatContext()
  const styles = useStyles()

  const sections = Object.entries(chat?.sections ?? {})
    .sort(([, a], [, b]) => (a.order ?? 0) - (b.order ?? 0))
    .map(([idx, section]) => ({
      key: idx,
      source: section.image.src ? { uri: section.image.src } : undefined,
    }))

  const onDragRelease = async ({
    itemOrder,
  }: {
    itemOrder: {
      key: string
      order: number
    }[]
  }) => {
    if (!chat) {
      return
    }

    // Skip if the same order
    const original = sections.map((o) => o.key)
    const updated = itemOrder.map((o) => o.key)
    if (JSON.stringify(original) === JSON.stringify(updated)) {
      return
    }

    const updates = itemOrder.reduce((accumulator: Record<string, number>, item) => {
      accumulator[`sections.${item.key}.order`] = item.order
      return accumulator
    }, {})

    await updateDoc(doc(firestore, chat.path), updates)
  }

  return (
    <View style={styles.inner}>
      <View style={styles.content}>
        <Text style={styles.subtitle}>Drag chat frames to reorder</Text>
        <View style={styles.orderContainer}>
          <Text></Text>
          <SortableGrid itemsPerRow={chat?.layout.columns} onDragRelease={onDragRelease}>
            {sections.map(({ key, source }) => {
              return (
                <View key={key} style={styles.chatSectionContainer}>
                  <View style={styles.chatSection}>
                    {source && (
                      <CacheableImage
                        source={source}
                        style={{ width: '100%', height: '100%' }}
                        contentFit="contain"
                        pointerEvents="none"
                      />
                    )}
                  </View>
                </View>
              )
            })}
          </SortableGrid>
        </View>
      </View>
    </View>
  )
}

const useStyles = () => {
  const { theme, mode } = useTheme()
  return ScaledSheet.create({
    ...getModalStyleDefinition(theme, mode),
    label: {
      fontSize: '10@ms0.3',
      fontWeight: '500',
      marginBottom: '3@mvs',
      color: theme[500],
    },
    orderContainer: {
      width: Math.floor(Math.max(Dimensions.get('window').width / 3.5, mvs(280))),
      alignSelf: 'center',
    },
    chatSectionContainer: {
      flex: 1,
      padding: '2@mvs',
    },
    chatSection: {
      flex: 1,
      borderColor: theme[200],
      borderWidth: 1,
      backgroundColor: theme.white,
    },
  })
}
